/**
 * Curated lists for the "Add a font" modal.
 *
 * - Google Fonts: a focused subset, especially Korean-friendly ones.
 *   Search filters by family name and category. Loaded via Google's CSS API
 *   so users can pick anything in the list without an API key.
 * - 누누폰트(noonnu.cc): aggregator of free Korean fonts hosted by their
 *   creators (NAVER, KAKAO, schools, brands). Each entry has a webfont URL
 *   discovered from noonnu's recommended embed snippets.
 *
 * This isn't an exhaustive index of either site — it's a curated starter
 * pack. Users can paste arbitrary CSS URLs in the "직접 추가" tab.
 */

export interface CuratedFont {
  family: string;
  cssUrl: string;
  category?: string;
  hint?: string;
}

export const GOOGLE_KOREAN: CuratedFont[] = [
  { family: 'Nanum Gothic', cssUrl: 'https://fonts.googleapis.com/css2?family=Nanum+Gothic:wght@400;700;800&display=swap', category: 'sans', hint: '나눔고딕' },
  { family: 'Nanum Myeongjo', cssUrl: 'https://fonts.googleapis.com/css2?family=Nanum+Myeongjo:wght@400;700;800&display=swap', category: 'serif', hint: '나눔명조' },
  { family: 'Nanum Pen Script', cssUrl: 'https://fonts.googleapis.com/css2?family=Nanum+Pen+Script&display=swap', category: 'handwriting', hint: '나눔펜' },
  { family: 'Nanum Brush Script', cssUrl: 'https://fonts.googleapis.com/css2?family=Nanum+Brush+Script&display=swap', category: 'handwriting' },
  { family: 'Gugi', cssUrl: 'https://fonts.googleapis.com/css2?family=Gugi&display=swap', category: 'display' },
  { family: 'Gamja Flower', cssUrl: 'https://fonts.googleapis.com/css2?family=Gamja+Flower&display=swap', category: 'handwriting' },
  { family: 'Dokdo', cssUrl: 'https://fonts.googleapis.com/css2?family=Dokdo&display=swap', category: 'handwriting' },
  { family: 'East Sea Dokdo', cssUrl: 'https://fonts.googleapis.com/css2?family=East+Sea+Dokdo&display=swap', category: 'handwriting' },
  { family: 'Yeon Sung', cssUrl: 'https://fonts.googleapis.com/css2?family=Yeon+Sung&display=swap', category: 'handwriting' },
  { family: 'Song Myung', cssUrl: 'https://fonts.googleapis.com/css2?family=Song+Myung&display=swap', category: 'serif' },
  { family: 'Kirang Haerang', cssUrl: 'https://fonts.googleapis.com/css2?family=Kirang+Haerang&display=swap', category: 'display' },
  { family: 'Jua', cssUrl: 'https://fonts.googleapis.com/css2?family=Jua&display=swap', category: 'display' },
  { family: 'Do Hyeon', cssUrl: 'https://fonts.googleapis.com/css2?family=Do+Hyeon&display=swap', category: 'display' },
  { family: 'Black Han Sans', cssUrl: 'https://fonts.googleapis.com/css2?family=Black+Han+Sans&display=swap', category: 'display' },
  { family: 'Black And White Picture', cssUrl: 'https://fonts.googleapis.com/css2?family=Black+And+White+Picture&display=swap', category: 'display' },
  { family: 'Stylish', cssUrl: 'https://fonts.googleapis.com/css2?family=Stylish&display=swap', category: 'sans' },
  { family: 'Sunflower', cssUrl: 'https://fonts.googleapis.com/css2?family=Sunflower:wght@300;500;700&display=swap', category: 'sans' },
  { family: 'Hi Melody', cssUrl: 'https://fonts.googleapis.com/css2?family=Hi+Melody&display=swap', category: 'handwriting' },
  { family: 'Diphylleia', cssUrl: 'https://fonts.googleapis.com/css2?family=Diphylleia&display=swap', category: 'serif' },
];

export const GOOGLE_LATIN: CuratedFont[] = [
  { family: 'Inter', cssUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;900&display=swap', category: 'sans' },
  { family: 'Roboto', cssUrl: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap', category: 'sans' },
  { family: 'Roboto Mono', cssUrl: 'https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500;700&display=swap', category: 'mono' },
  { family: 'Open Sans', cssUrl: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700;800&display=swap', category: 'sans' },
  { family: 'Lato', cssUrl: 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap', category: 'sans' },
  { family: 'Montserrat', cssUrl: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;700;900&display=swap', category: 'sans' },
  { family: 'Poppins', cssUrl: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700;900&display=swap', category: 'sans' },
  { family: 'Outfit', cssUrl: 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;700;900&display=swap', category: 'sans' },
  { family: 'Plus Jakarta Sans', cssUrl: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;700;800&display=swap', category: 'sans' },
  { family: 'Space Grotesk', cssUrl: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;700&display=swap', category: 'sans' },
  { family: 'DM Sans', cssUrl: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap', category: 'sans' },
  { family: 'Manrope', cssUrl: 'https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;700;800&display=swap', category: 'sans' },
  { family: 'Bebas Neue', cssUrl: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap', category: 'display' },
  { family: 'Anton', cssUrl: 'https://fonts.googleapis.com/css2?family=Anton&display=swap', category: 'display' },
  { family: 'Archivo Black', cssUrl: 'https://fonts.googleapis.com/css2?family=Archivo+Black&display=swap', category: 'display' },
  { family: 'Oswald', cssUrl: 'https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;600;700&display=swap', category: 'sans' },
  { family: 'Playfair Display', cssUrl: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&display=swap', category: 'serif' },
  { family: 'Merriweather', cssUrl: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700;900&display=swap', category: 'serif' },
  { family: 'Lora', cssUrl: 'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap', category: 'serif' },
  { family: 'Caveat', cssUrl: 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;700&display=swap', category: 'handwriting' },
  { family: 'Pacifico', cssUrl: 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap', category: 'handwriting' },
  { family: 'Permanent Marker', cssUrl: 'https://fonts.googleapis.com/css2?family=Permanent+Marker&display=swap', category: 'handwriting' },
  { family: 'JetBrains Mono', cssUrl: 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&display=swap', category: 'mono' },
  { family: 'Fira Code', cssUrl: 'https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500;700&display=swap', category: 'mono' },
];

/**
 * 누누폰트(noonnu.cc) 큐레이션. URL은 noonnu가 안내하는 webfont CDN 또는 NAVER/KAKAO 직접 URL.
 * 안정성을 위해 잘 알려진 무료 한국어 폰트만 포함.
 */
export const NOONNU_FONTS: CuratedFont[] = [
  {
    family: 'Pretendard',
    cssUrl: 'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.css',
    category: 'sans',
    hint: '인타 기본',
  },
  {
    family: 'Cafe24Ssurround',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2105_2@1.0/Cafe24Ssurround.woff',
    category: 'display',
    hint: '카페24 써라운드 (volume display)',
  },
  {
    family: 'Cafe24Ohsquare',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_two@1.0/Cafe24Ohsquare.woff',
    category: 'display',
    hint: '카페24 오스퀘어',
  },
  {
    family: 'BMHANNAPro',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_twelve@1.1/BMHANNAPro.woff',
    category: 'display',
    hint: '배민 한나 Pro',
  },
  {
    family: 'BMJUA',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_one@1.0/BMJUA.woff',
    category: 'display',
    hint: '배민 주아',
  },
  {
    family: 'BMDOHYEON',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_one@1.0/BMDOHYEON.woff',
    category: 'display',
    hint: '배민 도현',
  },
  {
    family: 'KOTRAHOPE',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2206-02@1.0/KOTRAHOPE.woff',
    category: 'sans',
    hint: 'KOTRA 희망체',
  },
  {
    family: 'GmarketSansMedium',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2102@1.1/GmarketSansMedium.woff',
    category: 'sans',
    hint: '지마켓산스 미디엄',
  },
  {
    family: 'TmoneyRoundWindRegular',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2107@1.1/TmoneyRoundWindRegular.woff',
    category: 'sans',
    hint: '티머니 둥근바람',
  },
  {
    family: 'EF_jejudoldam',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2304@1.1/EF_jejudoldam.woff2',
    category: 'display',
    hint: '제주돌담체',
  },
  {
    family: 'TheJamsil5Bold',
    cssUrl: 'https://cdn.jsdelivr.net/gh/projectnoonnu/noonfonts_2107@1.1/TheJamsil5Bold.woff',
    category: 'sans',
    hint: '잠실체 5 Bold',
  },
];

/** noonnu CDN file URL (woff/woff2) → @font-face wrapper. */
export function noonnuToFontUrl(font: CuratedFont): string | null {
  if (font.cssUrl.endsWith('.css')) return null; // already a stylesheet
  return font.cssUrl;
}
